/**
 * Theme Color Utilities
 * 
 * Centralized theme colors for consistent dark/light theme support
 * Based on Tailwind CSS classes and custom CSS variables
 */

export const themeColors = {
  // Primary brand color
  primary: {
    DEFAULT: '#1756fd', // --color-primary
    hover: '#144ee6',   // --color-primary-hover
    active: '#0e3bcc',  // --color-primary-active
    soft: 'rgba(23, 86, 253, 0.08)', // light mode
    softDark: 'rgba(23, 86, 253, 0.16)', // dark mode
  },

  // Accent colors
  accent: {
    teal: '#14b8a6',
    orange: '#fb923c',
  },

  // Text colors (Tailwind classes)
  text: {
    primary: 'text-zinc-900 dark:text-zinc-200',
    secondary: 'text-zinc-600 dark:text-zinc-400',
    tertiary: 'text-zinc-500 dark:text-zinc-500',
    inverse: 'text-white dark:text-zinc-900',
  },

  // Background colors (Tailwind classes)
  bg: {
    primary: 'bg-white dark:bg-zinc-900',
    secondary: 'bg-zinc-50 dark:bg-zinc-800',
    tertiary: 'bg-zinc-100 dark:bg-zinc-700',
    card: 'bg-white dark:bg-zinc-900',
    input: 'bg-white dark:bg-zinc-800',
    hover: 'hover:bg-zinc-50 dark:hover:bg-zinc-800',
  },

  // Border colors (Tailwind classes)
  border: {
    DEFAULT: 'border-zinc-200 dark:border-zinc-800',
    light: 'border-zinc-100 dark:border-zinc-900',
    strong: 'border-zinc-300 dark:border-zinc-700',
  },

  // Status colors
  status: {
    success: {
      bg: 'bg-green-100 dark:bg-green-900/30',
      border: 'border-green-200 dark:border-green-800',
      text: 'text-green-900 dark:text-green-100',
    },
    error: {
      bg: 'bg-red-100 dark:bg-red-900/30',
      border: 'border-red-200 dark:border-red-800',
      text: 'text-red-900 dark:text-red-100',
    },
    warning: {
      bg: 'bg-yellow-100 dark:bg-yellow-900/30',
      border: 'border-yellow-200 dark:border-yellow-800',
      text: 'text-yellow-900 dark:text-yellow-100',
    },
    info: {
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      border: 'border-blue-200 dark:border-blue-800',
      text: 'text-blue-900 dark:text-blue-100',
    },
  },
};

/**
 * Get theme-aware class names
 */
export function getThemeClasses(type, variant = 'DEFAULT') {
  const path = type.split('.');
  let value = themeColors;
  
  for (const key of path) {
    value = value[key];
    if (!value) return '';
  }
  
  if (typeof value === 'object' && variant in value) {
    return value[variant];
  }
  
  return typeof value === 'string' ? value : '';
}

/**
 * Combine theme classes
 */
export function combineThemeClasses(...classes) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Button variants with theme support
 */
export const buttonVariants = {
  primary: 'bg-primary text-white hover:bg-primary-hover active:bg-primary-active',
  secondary: combineThemeClasses(
    'bg-zinc-100 dark:bg-zinc-800',
    'text-zinc-900 dark:text-zinc-100',
    'hover:bg-zinc-200 dark:hover:bg-zinc-700',
    'border border-zinc-200 dark:border-zinc-700'
  ),
  outline: combineThemeClasses(
    'bg-transparent',
    'text-zinc-900 dark:text-zinc-100',
    'border-2 border-zinc-300 dark:border-zinc-700',
    'hover:bg-zinc-50 dark:hover:bg-zinc-800'
  ),
  ghost: combineThemeClasses(
    'bg-transparent',
    'text-zinc-900 dark:text-zinc-100',
    'hover:bg-zinc-100 dark:hover:bg-zinc-800'
  ),
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
  success: 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800',
};

/**
 * Card variants with theme support
 */
export const cardVariants = {
  default: combineThemeClasses(
    'bg-white dark:bg-zinc-900',
    'border border-zinc-200 dark:border-zinc-800',
    'rounded-lg shadow-sm'
  ),
  elevated: combineThemeClasses(
    'bg-white dark:bg-zinc-900',
    'border border-zinc-200 dark:border-zinc-800',
    'rounded-lg shadow-md'
  ),
  flat: combineThemeClasses(
    'bg-zinc-50 dark:bg-zinc-800',
    'rounded-lg'
  ),
};

/**
 * Input variants with theme support
 */
export const inputVariants = {
  default: combineThemeClasses(
    'bg-white dark:bg-zinc-800',
    'border border-zinc-300 dark:border-zinc-700',
    'text-zinc-900 dark:text-zinc-100',
    'placeholder:text-zinc-400 dark:placeholder:text-zinc-500',
    'focus:border-primary focus:ring-2 focus:ring-primary/20'
  ),
  error: combineThemeClasses(
    'bg-white dark:bg-zinc-800',
    'border-2 border-red-500 dark:border-red-600',
    'text-zinc-900 dark:text-zinc-100',
    'focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
  ),
};

/**
 * Get current theme (light/dark)
 */
export function getCurrentTheme() {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

/**
 * Toggle theme
 */
export function toggleTheme() {
  const html = document.documentElement;
  const isDark = html.classList.contains('dark');
  
  if (isDark) {
    html.classList.remove('dark');
    localStorage.setItem('theme', 'light');
  } else {
    html.classList.add('dark');
    localStorage.setItem('theme', 'dark');
  }
  
  return !isDark ? 'dark' : 'light';
}

/**
 * Set theme
 */
export function setTheme(theme) {
  const html = document.documentElement;
  
  if (theme === 'dark') {
    html.classList.add('dark');
  } else {
    html.classList.remove('dark');
  }
  
  localStorage.setItem('theme', theme);
}

/**
 * Initialize theme from localStorage
 * Default: dark mode (no system preference detection)
 */
export function initializeTheme() {
  const savedTheme = localStorage.getItem('theme');
  
  if (savedTheme === 'light') {
    setTheme('light');
  } else {
    // Default to dark mode
    setTheme('dark');
  }
}

export default themeColors;
